'use client';

import { useState, useEffect, useRef } from 'react';
import { useParams } from 'next/navigation';
import Link from 'next/link';
import { posthog } from '@/lib/posthog';
import { ArrowRight, CalendarBlank as Calendar, ChefHat, Clock, ForkKnife as Utensils, Heart, Leaf, MapPin, Medal as Award, Phone, Sparkle as Sparkles, Star, Trophy } from '@phosphor-icons/react/dist/ssr';
import { ThemeProvider } from '@/components/public/theme-provider';
import { HeroBanner } from '@/components/public/hero-banner';
import { PublicNavbar } from '@/components/public/public-navbar';
import { PublicSEO } from '@/components/public/public-seo';
import { resolveThemeConfig } from '@/types/theme';
import { getContextualHref } from '@/lib/domains';
import { formatBusinessHoursRange } from '@/lib/business-hours';
import { OffersBanner } from '@/components/public/offers-banner';
import { PoweredByDineator } from '@/components/public/powered-by-dineator';
import { Widgets } from '@/components/public/widgets/Widgets';

const API_BASE = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000/api';

interface FeatureItem { icon: string; title: string; desc: string; }
interface TestimonialItem { name: string; body: string; rating: number; }

function useReveal(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return { ref, visible };
}

function Reveal({ children, delay = 0, className, dir = 'up' }: {
  children: React.ReactNode; delay?: number; className?: string;
  dir?: 'up' | 'left' | 'right' | 'scale';
}) {
  const { ref, visible } = useReveal();
  const transform = {
    up: visible ? 'translateY(0)' : 'translateY(30px)',
    left: visible ? 'translateX(0)' : 'translateX(-30px)',
    right: visible ? 'translateX(0)' : 'translateX(30px)',
    scale: visible ? 'scale(1)' : 'scale(0.93)',
  }[dir];
  return (
    <div ref={ref} className={className} style={{
      opacity: visible ? 1 : 0, transform,
      transition: `opacity 0.65s cubic-bezier(.16,1,.3,1) ${delay}ms, transform 0.65s cubic-bezier(.16,1,.3,1) ${delay}ms`,
    }}>
      {children}
    </div>
  );
}

/** In browse-only mode (super-admin switch) an "Order ..." call to action reads "View Menu" instead. */
function browseLabel(label: string | undefined, fallback: string, orderingEnabled: boolean) {
  if (!orderingEnabled && label && /order/i.test(label)) return 'View Menu';
  return label || fallback;
}

function safeUrl(url?: string | null): string | null {
  if (!url) return null;
  try { const { protocol } = new URL(url); return (protocol === 'https:' || protocol === 'http:') ? url : null; }
  catch { return null; }
}

function FeatureIcon({ name, size = 24 }: { name: string; size?: number }) {
  if (name === 'Leaf') return <Leaf size={size} />;
  if (name === 'Award') return <Award size={size} />;
  if (name === 'Star') return <Star size={size} />;
  if (name === 'Clock') return <Clock size={size} />;
  if (name === 'Heart') return <Heart size={size} />;
  if (name === 'Trophy') return <Trophy size={size} />;
  return <ChefHat size={size} />;
}

function Eyebrow({ label, center = false }: { label: string; center?: boolean }) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: '10px',
      justifyContent: center ? 'center' : 'flex-start',
      fontSize: '10px', fontWeight: 800, letterSpacing: '0.16em',
      textTransform: 'uppercase', color: 'var(--color-primary)',
      marginBottom: '14px',
    }}>
      <span style={{ width: '22px', height: '2px', background: 'var(--color-primary)', borderRadius: '1px', flexShrink: 0 }} />
      {label}
      {center && <span style={{ width: '22px', height: '2px', background: 'var(--color-primary)', borderRadius: '1px', flexShrink: 0 }} />}
    </div>
  );
}

const GLOBAL_CSS = `
  @keyframes spin { to { transform: rotate(360deg) } }
  @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
  @keyframes blob-a { 0%,100% { transform: translate(0,0) scale(1); } 40% { transform: translate(40px,-25px) scale(1.07); } 70% { transform: translate(-20px,15px) scale(0.96); } }
  @keyframes blob-b { 0%,100% { transform: translate(0,0) scale(1); } 35% { transform: translate(-30px,35px) scale(0.97); } 65% { transform: translate(25px,-20px) scale(1.05); } }
  @keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
  @keyframes pulse-ring { 0% { transform: scale(0.85); opacity: 0.7; } 100% { transform: scale(1.6); opacity: 0; } }
  @keyframes hero-in { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
  .hero-text-gradient {
    background: linear-gradient(160deg, #ffffff 0%, rgba(255,255,255,0.72) 100%);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
  }
  .primary-btn { transition: transform 0.2s cubic-bezier(.16,1,.3,1), box-shadow 0.2s !important; }
  .primary-btn:hover { transform: translateY(-2px) !important; box-shadow: 0 14px 40px rgba(0,0,0,0.25) !important; }
  .ghost-btn { transition: background 0.2s, transform 0.2s cubic-bezier(.16,1,.3,1) !important; }
  .ghost-btn:hover { background: rgba(255,255,255,0.22) !important; transform: translateY(-2px) !important; }
  .outline-btn { transition: all 0.2s cubic-bezier(.16,1,.3,1) !important; }
  .outline-btn:hover { background: var(--color-primary) !important; color: var(--color-primary-fg) !important; border-color: var(--color-primary) !important; transform: translateY(-2px) !important; }
  .feature-card { transition: transform 0.25s cubic-bezier(.16,1,.3,1), box-shadow 0.25s !important; cursor: default; }
  .feature-card:hover { transform: translateY(-6px) !important; box-shadow: 0 20px 60px rgba(0,0,0,0.12) !important; }
  .gallery-item img { transition: transform 0.45s cubic-bezier(.16,1,.3,1) !important; }
  .gallery-item:hover img { transform: scale(1.07) !important; }
  .gallery-item .g-overlay { transition: opacity 0.3s !important; }
  .gallery-item:hover .g-overlay { opacity: 1 !important; }
  .testimonial-card { transition: transform 0.25s cubic-bezier(.16,1,.3,1), box-shadow 0.25s !important; }
  .testimonial-card:hover { transform: translateY(-4px) !important; box-shadow: 0 16px 48px rgba(0,0,0,0.11) !important; }
  .info-card { transition: transform 0.2s, border-color 0.2s !important; }
  .info-card:hover { transform: translateY(-3px) !important; border-color: var(--color-primary) !important; }
  .footer-link { transition: color 0.18s !important; }
  .footer-link:hover { color: var(--color-primary) !important; }
`;

// Fallback only — used if a restaurant has trustStripEnabled on but somehow an
// empty trustStripItems (e.g. themeConfig saved before this field existed).
const DEFAULT_TRUST_ITEMS = [
  'Exceptional Quality', 'Fresh Ingredients', 'Expert Chefs',
  'Award Winning', 'Unforgettable Experience', 'Guest Favorite', 'Made With Passion',
];

export default function UnifiedHomeRenderer({ initialData }: { initialData?: any }) {
  const params = useParams();
  const slug = (params?.slug as string) || initialData?.restaurant?.slug || '';
  const [fetchedData, setData] = useState<any>(null);
  const data = initialData ?? fetchedData; // a changed initialData prop wins (the live theme preview streams drafts through it)
  const [isLoading, setIsLoading] = useState(!initialData);

  useEffect(() => {
    if (!slug || initialData) return;
    fetch(`${API_BASE}/menu/public/${slug}`)
      .then(r => r.json())
      .then(res => { if (res.success && res.data) setData(res.data); })
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, [slug, initialData]);

  const theme = resolveThemeConfig(data?.themeConfig ?? null);
  const { tokens } = theme;
  const s = tokens.sections;
  const restaurant = data?.restaurant;
  const featureFlags = data?.featureFlags;
  const safeLogoUrl = safeUrl(restaurant?.logoUrl);

  // Track qr_scanned once per browser session per slug
  useEffect(() => {
    if (!slug) return;
    const sessionKey = `rf_qr_scanned_${slug}`;
    if (sessionStorage.getItem(sessionKey)) return;
    posthog.capture('qr_scanned', { restaurant_slug: slug });
    sessionStorage.setItem(sessionKey, '1');
  }, [slug]);

  const featureItems: FeatureItem[] = (() => {
    try { return s.featuresItems ? JSON.parse(s.featuresItems) : []; } catch { return []; }
  })();
  const testimonials: TestimonialItem[] = (() => {
    try { return s.testimonialsItems ? JSON.parse(s.testimonialsItems) : []; } catch { return []; }
  })();
  const galleryUrls: string[] = s.galleryImages
    ? s.galleryImages.split(',').map((u: string) => u.trim()).filter(Boolean)
    : [];

  if (isLoading) {
    return (
      <ThemeProvider config={theme}>
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
        <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px', background: 'var(--color-bg)' }}>
          <div style={{ width: '44px', height: '44px', borderRadius: '50%', border: '3px solid var(--color-border)', borderTopColor: 'var(--color-primary)', animation: 'spin 0.8s linear infinite' }} />
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', fontWeight: 500 }}>Loading…</p>
        </div>
      </ThemeProvider>
    );
  }

  if (!data || !restaurant) {
    return (
      <ThemeProvider config={theme}>
        <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px', padding: '24px', background: 'var(--color-bg)' }}>
          <Utensils size={36} style={{ color: 'var(--color-text-muted)', opacity: 0.35 }} />
          <p style={{ fontWeight: 600, color: 'var(--color-text-muted)' }}>Restaurant not found</p>
        </div>
      </ThemeProvider>
    );
  }

  const wctx = { slug, restaurant, data, currency: data?.restaurant?.config?.currency };
  const slots = { start: <Widgets widgets={tokens.widgets} page="home" slot="start" ctx={wctx} />, end: <Widgets widgets={tokens.widgets} page="home" slot="end" ctx={wctx} /> };

  return (
    <ThemeProvider config={theme}>
      <style>{GLOBAL_CSS}</style>
      <PublicSEO restaurant={restaurant} tokens={tokens} pageType="home" />
      <OffersBanner restaurantId={restaurant.id} apiBase={API_BASE} tokens={tokens} />
      <div className="theme-body" style={{ minHeight: '100dvh', background: 'var(--color-bg)', overflowX: 'hidden' }}>

        <PublicNavbar
          restaurant={{ ...restaurant, logoUrl: safeLogoUrl || undefined, config: { enableReservations: featureFlags?.enableReservations } }}
          tokens={tokens}
          activePage="home"
        />

        {s.showHeroBanner
          ? <HeroBanner restaurant={restaurant} tokens={tokens} />
          : s.homeHeroEnabled && (
            <HomeHero style={tokens.layout.heroStyle} slug={slug} restaurant={restaurant} s={s} featureFlags={featureFlags} safeLogoUrl={safeLogoUrl} />
          )
        }

        {slots.start}

        {s.trustStripEnabled !== false && <TrustStrip items={s.trustStripItems} />}

        {s.aboutEnabled && <AboutSection slug={slug} s={s} />}

        {s.featuresEnabled && featureItems.length > 0 && (
          <FeaturesSection s={s} featureItems={featureItems} variant={tokens.layout.sectionStyle} />
        )}

        {s.galleryEnabled && galleryUrls.length > 0 && (
          <GallerySection s={s} galleryUrls={galleryUrls} />
        )}

        {s.testimonialsEnabled && testimonials.length > 0 && (
          <TestimonialsSection s={s} testimonials={testimonials} />
        )}

        {s.ctaEnabled && (
          <CTASection slug={slug} s={s} featureFlags={featureFlags} variant={tokens.layout.sectionStyle} />
        )}

        {slots.end}

        <ContactSection restaurant={restaurant} data={data} />
        <HomeFooter restaurant={restaurant} slug={slug} featureFlags={featureFlags} safeLogoUrl={safeLogoUrl} />

      </div>
    </ThemeProvider>
  );
}

/** Shared headline, subtitle and buttons for the non-centred hero layouts. */
function HeroCopy({ slug, restaurant, s, featureFlags, onDark, align = 'left' }: any) {
  const fg = onDark ? '#fff' : 'var(--color-text)';
  const sub = onDark ? 'rgba(255,255,255,0.82)' : 'var(--color-text-muted)';
  const center = align === 'center';
  const ordering = featureFlags?.orderingEnabled !== false;
  return (
    <div style={{ textAlign: center ? 'center' : 'left', color: fg }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', fontSize: '11px', fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: onDark ? 'rgba(255,255,255,0.8)' : 'var(--color-primary)', marginBottom: '20px' }}>
        <span style={{ width: '28px', height: '2px', background: 'currentColor' }} /> Welcome
      </div>
      <h1 style={{ fontSize: 'clamp(2.6rem, 7vw, 5rem)', fontWeight: 'var(--font-heading-weight)', letterSpacing: '-0.035em', lineHeight: 1.04, margin: '0 0 20px' }}>
        {s.homeHeroTitle || restaurant.name}
      </h1>
      {(s.homeHeroSubtitle || restaurant.description) && (
        <p style={{ fontSize: 'clamp(1rem, 2.2vw, 1.15rem)', color: sub, lineHeight: 1.7, margin: center ? '0 auto 36px' : '0 0 36px', maxWidth: '520px' }}>
          {s.homeHeroSubtitle || restaurant.description}
        </p>
      )}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: center ? 'center' : 'flex-start' }}>
        <Link href={getContextualHref(slug, '/menu')} style={{ textDecoration: 'none' }}>
          <button className="primary-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '14px 30px', borderRadius: 'var(--radius)', background: onDark ? '#fff' : 'var(--color-primary)', color: onDark ? 'var(--color-primary)' : 'var(--color-primary-fg)', fontWeight: 800, fontSize: '0.95rem', border: 'none', cursor: 'pointer' }}>
            <Utensils size={16} /> {browseLabel(s.homeHeroCTALabel, 'View Menu', ordering)}
          </button>
        </Link>
        {featureFlags?.enableReservations && (
          <Link href={getContextualHref(slug, '/reserve')} style={{ textDecoration: 'none' }}>
            <button className="ghost-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '14px 30px', borderRadius: 'var(--radius)', background: 'transparent', color: fg, fontWeight: 700, fontSize: '0.95rem', border: `1.5px solid ${onDark ? 'rgba(255,255,255,0.55)' : 'var(--color-border)'}`, cursor: 'pointer' }}>
              <Calendar size={16} /> {s.homeHeroCTA2Label || 'Reserve a Table'}
            </button>
          </Link>
        )}
      </div>
    </div>
  );
}

function heroBackdrop(s: any, angle = 135) {
  return s.homeHeroImageUrl
    ? `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.62)), url(${s.homeHeroImageUrl}) center/cover no-repeat`
    : `linear-gradient(${angle}deg, var(--color-primary) 0%, color-mix(in srgb, var(--color-primary) 45%, var(--color-accent)) 100%)`;
}

function HomeHero(props: any) {
  const { style = 'centered', ...rest } = props;
  const { s, restaurant, safeLogoUrl } = rest;
  const pad = 'clamp(56px,8vw,104px) var(--density-padding)';

  if (style === 'split') {
    return (
      <section style={{ background: 'var(--color-bg)', overflow: 'hidden' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', alignItems: 'stretch', minHeight: 'min(80dvh, 640px)' }}>
          <div style={{ padding: pad, display: 'flex', alignItems: 'center' }}><HeroCopy {...rest} /></div>
          <div style={{ position: 'relative', minHeight: '320px', background: s.homeHeroImageUrl ? `url(${s.homeHeroImageUrl}) center/cover no-repeat` : heroBackdrop(s, 160), borderRadius: 'calc(var(--radius) * 2)', margin: 'clamp(16px,3vw,32px)', display: 'grid', placeItems: 'center', color: '#fff', overflow: 'hidden' }}>
            {!s.homeHeroImageUrl && (safeLogoUrl
              ? <img src={safeLogoUrl} alt={restaurant.name} style={{ width: '38%', maxWidth: '180px', aspectRatio: '1', objectFit: 'cover', borderRadius: '50%', border: '3px solid rgba(255,255,255,0.6)' }} />
              : <span style={{ fontSize: 'clamp(6rem, 18vw, 11rem)', fontWeight: 'var(--font-heading-weight)', opacity: 0.9, lineHeight: 1 }}>{(restaurant.name || '?').charAt(0)}</span>)}
          </div>
        </div>
      </section>
    );
  }

  if (style === 'left') {
    return (
      <section style={{ position: 'relative', overflow: 'hidden', background: heroBackdrop(s, 110), minHeight: 'calc(88dvh - 60px)', display: 'flex', alignItems: 'center', padding: pad }}>
        <div style={{ position: 'absolute', right: '-12%', top: '50%', width: 'min(70vw, 640px)', aspectRatio: '1', transform: 'translateY(-50%)', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.18)', boxShadow: 'inset 0 0 0 40px rgba(255,255,255,0.04)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', maxWidth: '1280px', width: '100%', margin: '0 auto' }}>
          <div style={{ maxWidth: '680px' }}><HeroCopy {...rest} onDark /></div>
        </div>
      </section>
    );
  }

  if (style === 'framed') {
    return (
      <section style={{ background: 'var(--color-bg)', padding: 'clamp(12px,2.5vw,28px)' }}>
        <div style={{ borderRadius: 'calc(var(--radius) * 2.2)', background: heroBackdrop(s, 150), minHeight: 'calc(78dvh - 60px)', display: 'grid', placeItems: 'center', padding: pad, boxShadow: 'var(--shadow-card)' }}>
          <div style={{ maxWidth: '760px' }}><HeroCopy {...rest} onDark align="center" /></div>
        </div>
      </section>
    );
  }

  if (style === 'minimal') {
    return (
      <section style={{ background: 'var(--color-bg)', padding: 'clamp(72px,11vw,150px) var(--density-padding) clamp(56px,8vw,110px)', borderBottom: '1px solid var(--color-border)' }}>
        <div style={{ maxWidth: '820px', margin: '0 auto' }}>
          <HeroCopy {...rest} align="center" />
          <div style={{ width: '64px', height: '3px', background: 'var(--color-accent)', margin: '48px auto 0', borderRadius: '2px' }} />
        </div>
      </section>
    );
  }

  return <HomeHeroCentered {...rest} />;
}

function HomeHeroCentered({ slug, restaurant, s, featureFlags, safeLogoUrl }: any) {
  const hasImage = !!s.homeHeroImageUrl;
  return (
    <section style={{
      position: 'relative', minHeight: 'calc(92dvh - 60px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      textAlign: 'center', overflow: 'hidden', color: '#fff',
      padding: 'clamp(72px,10vw,120px) var(--density-padding) clamp(80px,10vw,120px)',
      background: hasImage
        ? `url(${s.homeHeroImageUrl}) center/cover no-repeat`
        : `linear-gradient(135deg, var(--color-primary) 0%, var(--color-accent) 100%)`,
    }}>
      {hasImage && (
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.22) 0%, rgba(0,0,0,0.58) 55%, rgba(0,0,0,0.78) 100%)' }} />
      )}
      {!hasImage && (
        <>
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', backgroundImage: 'radial-gradient(circle at 20% 55%, rgba(255,255,255,0.07) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(0,0,0,0.07) 0%, transparent 40%)' }} />
          <div style={{ position: 'absolute', width: '700px', height: '700px', borderRadius: '50%', top: '-250px', left: '-250px', background: 'rgba(255,255,255,0.06)', filter: 'blur(100px)', animation: 'blob-a 16s ease-in-out infinite', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', width: '600px', height: '600px', borderRadius: '50%', bottom: '-200px', right: '-200px', background: 'rgba(0,0,0,0.1)', filter: 'blur(90px)', animation: 'blob-b 20s ease-in-out infinite', pointerEvents: 'none' }} />
        </>
      )}
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.055) 1px, transparent 1px)', backgroundSize: '36px 36px', pointerEvents: 'none' }} />

      <div style={{ position: 'relative', zIndex: 2, maxWidth: '820px', margin: '0 auto' }}>
        {safeLogoUrl && (
          <div style={{ position: 'relative', display: 'inline-block', marginBottom: '32px', animation: 'float 5s ease-in-out infinite, hero-in 0.7s ease both' }}>
            <div style={{ position: 'absolute', inset: '-10px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.22)', animation: 'pulse-ring 3s ease-out infinite' }} />
            <img src={safeLogoUrl} alt={restaurant.name} fetchPriority="high" style={{ width: '104px', height: '104px', borderRadius: '50%', objectFit: 'cover', border: '3.5px solid rgba(255,255,255,0.6)', boxShadow: '0 16px 52px rgba(0,0,0,0.35)', display: 'block' }} />
          </div>
        )}
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.22)', borderRadius: '100px', padding: '6px 18px', fontSize: '10px', fontWeight: 800, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.92)', marginBottom: '22px', animation: 'hero-in 0.6s ease 0.05s both' }}>
          <Sparkles size={10} /> Welcome
        </div>
        <h1 className="hero-text-gradient" style={{ fontSize: 'clamp(2.8rem, 8vw, 5.5rem)', fontWeight: 'var(--font-heading-weight)', letterSpacing: '-0.04em', lineHeight: 1.02, margin: '0 0 20px', animation: 'hero-in 0.65s ease 0.1s both' }}>
          {s.homeHeroTitle || restaurant.name}
        </h1>
        {(s.homeHeroSubtitle || restaurant.description) && (
          <p style={{ fontSize: 'clamp(1rem, 2.5vw, 1.18rem)', color: 'rgba(255,255,255,0.8)', lineHeight: 1.68, margin: '0 auto 40px', maxWidth: '540px', animation: 'hero-in 0.65s ease 0.18s both' }}>
            {s.homeHeroSubtitle || restaurant.description}
          </p>
        )}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', animation: 'hero-in 0.65s ease 0.26s both' }}>
          <Link href={getContextualHref(slug, '/menu')} style={{ textDecoration: 'none' }}>
            <button className="primary-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '15px 32px', borderRadius: 'var(--radius)', background: '#fff', color: 'var(--color-primary)', fontWeight: 800, fontSize: '0.95rem', border: 'none', cursor: 'pointer', boxShadow: '0 8px 32px rgba(0,0,0,0.22)' }}>
              <Utensils size={16} /> {browseLabel(s.homeHeroCTALabel, 'View Menu', featureFlags?.orderingEnabled !== false)}
            </button>
          </Link>
          {featureFlags?.enableReservations && (
            <Link href={getContextualHref(slug, '/reserve')} style={{ textDecoration: 'none' }}>
              <button className="ghost-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '15px 32px', borderRadius: 'var(--radius)', background: 'rgba(255,255,255,0.13)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', color: '#fff', fontWeight: 700, fontSize: '0.95rem', border: '1.5px solid rgba(255,255,255,0.32)', cursor: 'pointer' }}>
                <Calendar size={16} /> {s.homeHeroCTA2Label || 'Reserve a Table'}
              </button>
            </Link>
          )}
        </div>
        <div style={{ marginTop: '60px', display: 'flex', flexDirection: 'column', alignItems: 'center', opacity: 0.4, animation: 'hero-in 0.65s ease 0.4s both, float 2.5s ease-in-out 1s infinite' }}>
          <div style={{ width: '1.5px', height: '40px', background: 'rgba(255,255,255,0.65)', borderRadius: '1px' }} />
        </div>
      </div>
    </section>
  );
}

function TrustStrip({ items }: { items?: string }) {
  const parsed = (items ?? '').split(',').map((s) => s.trim()).filter(Boolean);
  const trustItems = parsed.length > 0 ? parsed : DEFAULT_TRUST_ITEMS;
  const doubled = [...trustItems, ...trustItems];
  return (
    <div style={{ background: 'var(--color-primary)', overflow: 'hidden', padding: '11px 0', borderTop: '1px solid rgba(0,0,0,0.08)', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
      <div style={{ display: 'flex', width: 'fit-content', animation: 'marquee 26s linear infinite', userSelect: 'none' }}>
        {doubled.map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '0 26px', whiteSpace: 'nowrap', fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.13em', textTransform: 'uppercase', color: 'var(--color-primary-fg)', opacity: 0.88 }}>
            <Star size={8} style={{ fill: 'currentColor', flexShrink: 0 }} />
            {item}
          </div>
        ))}
      </div>
    </div>
  );
}

function AboutSection({ slug, s }: any) {
  const hasImage = !!s.aboutImageUrl;
  return (
    <section style={{ padding: 'clamp(64px,8vw,100px) var(--density-padding)', maxWidth: '1280px', margin: '0 auto' }}>
      <div style={{ display: 'grid', gridTemplateColumns: hasImage ? 'repeat(auto-fit, minmax(300px, 1fr))' : '1fr', gap: 'clamp(40px,6vw,88px)', alignItems: 'center' }}>
        {hasImage && (
          <Reveal dir="left">
            <div style={{ position: 'relative' }}>
              <div style={{ borderRadius: 'calc(var(--radius) * 1.5)', overflow: 'hidden', boxShadow: '0 28px 88px rgba(0,0,0,0.14)', lineHeight: 0 }}>
                <img src={s.aboutImageUrl} alt="About" loading="lazy" decoding="async" style={{ width: '100%', height: 'clamp(300px, 40vw, 480px)', objectFit: 'cover', display: 'block' }} />
              </div>
            </div>
          </Reveal>
        )}
        <Reveal delay={hasImage ? 80 : 0} dir={hasImage ? 'right' : 'up'}>
          <div style={{ maxWidth: hasImage ? '100%' : '680px', margin: '0 auto' }}>
            <Eyebrow label="Our Story" />
            <h2 style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.6rem)', fontWeight: 'var(--font-heading-weight)', color: 'var(--color-text)', letterSpacing: '-0.03em', lineHeight: 1.18, margin: '0 0 20px' }}>
              {s.aboutTitle || 'About Us'}
            </h2>
            {s.aboutBody && (
              <p style={{ color: 'var(--color-text-muted)', lineHeight: 1.82, fontSize: '0.97rem', margin: '0 0 32px' }}>{s.aboutBody}</p>
            )}
            <Link href={getContextualHref(slug, '/menu')} style={{ textDecoration: 'none' }}>
              <button className="outline-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 26px', borderRadius: 'var(--radius)', background: 'transparent', color: 'var(--color-primary)', fontWeight: 700, fontSize: '0.9rem', border: '2px solid var(--color-primary)', cursor: 'pointer' }}>
                Explore Our Menu <ArrowRight size={15} />
              </button>
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function FeaturesSection({ s, featureItems, variant = 'cards' }: { s: any; featureItems: FeatureItem[]; variant?: string }) {
  if (variant !== 'cards') return <FeaturesAlt s={s} featureItems={featureItems} variant={variant} />;
  const gradients = [
    'linear-gradient(135deg, var(--color-primary) 0%, var(--color-accent) 100%)',
    'linear-gradient(135deg, var(--color-accent) 0%, var(--color-primary) 100%)',
    'linear-gradient(135deg, var(--color-primary) 0%, color-mix(in srgb, var(--color-primary) 55%, black) 100%)',
  ];
  return (
    <section style={{ background: 'var(--color-surface)', borderTop: '1px solid var(--color-border)', borderBottom: '1px solid var(--color-border)', padding: 'clamp(64px,8vw,100px) var(--density-padding)' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        <Reveal>
          <div style={{ textAlign: 'center', marginBottom: '52px' }}>
            <Eyebrow label={s.featuresSubtitle || 'Why Choose Us'} center />
            <h2 style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.6rem)', fontWeight: 'var(--font-heading-weight)', color: 'var(--color-text)', letterSpacing: '-0.03em', lineHeight: 1.15, margin: 0 }}>{s.featuresTitle || 'The Finest Dining Experience'}</h2>
          </div>
        </Reveal>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
          {featureItems.map((f, i) => (
            <Reveal key={i} delay={i * 70}>
              <div className="feature-card" style={{ background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 'calc(var(--radius) * 1.3)', padding: 'clamp(24px, 3vw, 36px)' }}>
                <div style={{ width: '58px', height: '58px', borderRadius: 'calc(var(--radius) * 1.1)', background: gradients[i % gradients.length], display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', marginBottom: '22px', boxShadow: '0 8px 24px rgba(0,0,0,0.14)' }}>
                  <FeatureIcon name={f.icon} size={24} />
                </div>
                <h3 style={{ fontWeight: 'var(--font-heading-weight)', fontSize: '1.05rem', color: 'var(--color-text)', margin: '0 0 10px', letterSpacing: '-0.01em' }}>{f.title}</h3>
                <p style={{ fontSize: '0.855rem', color: 'var(--color-text-muted)', lineHeight: 1.72, margin: 0 }}>{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturesAlt({ s, featureItems, variant }: { s: any; featureItems: FeatureItem[]; variant: string }) {
  const lined = variant === 'lined';
  const tiles = variant === 'tiles';
  return (
    <section style={{ background: tiles ? 'var(--color-bg)' : 'var(--color-surface)', borderTop: '1px solid var(--color-border)', padding: 'clamp(64px,8vw,100px) var(--density-padding)' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto', display: lined ? 'grid' : 'block', gridTemplateColumns: lined ? 'repeat(auto-fit, minmax(280px, 1fr))' : undefined, gap: '56px' }}>
        <Reveal>
          <div style={{ textAlign: tiles ? 'center' : 'left', marginBottom: lined ? 0 : '44px' }}>
            <Eyebrow label={s.featuresSubtitle || 'Why Choose Us'} center={tiles} />
            <h2 style={{ fontSize: 'clamp(1.9rem, 4vw, 3rem)', fontWeight: 'var(--font-heading-weight)', color: 'var(--color-text)', letterSpacing: '-0.03em', lineHeight: 1.1, margin: 0, maxWidth: '520px' }}>{s.featuresTitle || 'The Finest Dining Experience'}</h2>
          </div>
        </Reveal>
        <div style={{ display: 'grid', gridTemplateColumns: lined ? '1fr' : 'repeat(auto-fit, minmax(240px, 1fr))', gap: lined ? 0 : '16px' }}>
          {featureItems.map((f, i) => {
            const num = String(i + 1).padStart(2, '0');
            return (
              <Reveal key={i} delay={i * 70}>
                {lined ? (
                  <div style={{ display: 'flex', gap: '22px', alignItems: 'baseline', padding: '24px 0', borderTop: i === 0 ? '1px solid var(--color-text)' : '1px solid var(--color-border)' }}>
                    <span style={{ fontSize: '0.8rem', fontWeight: 800, color: 'var(--color-primary)', letterSpacing: '0.1em' }}>{num}</span>
                    <div>
                      <h3 style={{ fontWeight: 'var(--font-heading-weight)', fontSize: '1.2rem', color: 'var(--color-text)', margin: '0 0 8px' }}>{f.title}</h3>
                      <p style={{ fontSize: '0.9rem', color: 'var(--color-text-muted)', lineHeight: 1.7, margin: 0 }}>{f.desc}</p>
                    </div>
                  </div>
                ) : tiles ? (
                  <div style={{ background: i % 2 ? 'var(--color-accent)' : 'var(--color-primary)', color: i % 2 ? '#111' : 'var(--color-primary-fg)', borderRadius: 'calc(var(--radius) * 1.5)', padding: 'clamp(26px, 3vw, 38px)', height: '100%' }}>
                    <FeatureIcon name={f.icon} size={28} />
                    <h3 style={{ fontWeight: 'var(--font-heading-weight)', fontSize: '1.15rem', margin: '22px 0 10px' }}>{f.title}</h3>
                    <p style={{ fontSize: '0.88rem', opacity: 0.85, lineHeight: 1.7, margin: 0 }}>{f.desc}</p>
                  </div>
                ) : (
                  <div style={{ borderTop: '3px solid var(--color-primary)', background: 'var(--color-bg)', padding: 'clamp(24px, 3vw, 34px)', height: '100%' }}>
                    <div style={{ fontSize: 'clamp(2.6rem, 5vw, 3.6rem)', fontWeight: 'var(--font-heading-weight)', color: 'transparent', WebkitTextStroke: '1.5px var(--color-primary)', lineHeight: 1, marginBottom: '18px' }}>{num}</div>
                    <h3 style={{ fontWeight: 'var(--font-heading-weight)', fontSize: '1.1rem', color: 'var(--color-text)', margin: '0 0 10px' }}>{f.title}</h3>
                    <p style={{ fontSize: '0.88rem', color: 'var(--color-text-muted)', lineHeight: 1.7, margin: 0 }}>{f.desc}</p>
                  </div>
                )}
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function GallerySection({ s, galleryUrls }: { s: any; galleryUrls: string[] }) {
  return (
    <section style={{ padding: 'clamp(64px,8vw,100px) var(--density-padding)', maxWidth: '1280px', margin: '0 auto' }}>
      <Reveal>
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <Eyebrow label="Gallery" center />
          <h2 style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.6rem)', fontWeight: 'var(--font-heading-weight)', color: 'var(--color-text)', letterSpacing: '-0.03em', lineHeight: 1.15, margin: 0 }}>{s.galleryTitle || 'A Taste of Our World'}</h2>
        </div>
      </Reveal>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '10px' }}>
        {galleryUrls.map((url, i) => {
          const isHero = i === 0 && galleryUrls.length >= 3;
          return (
            <div key={i} style={{ gridColumn: isHero ? 'span 2' : 'span 1' }}>
              <Reveal delay={i * 55} dir="scale">
                <div className="gallery-item" style={{ borderRadius: 'calc(var(--radius) * 1.3)', overflow: 'hidden', lineHeight: 0, position: 'relative', cursor: 'pointer', aspectRatio: isHero ? '16/9' : '1' }}>
                  <img src={url} alt={`Gallery ${i + 1}`} loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  <div className="g-overlay" style={{ position: 'absolute', inset: 0, opacity: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.1) 50%, transparent 100%)', display: 'flex', alignItems: 'flex-end', padding: '16px 18px' }}>
                    <span style={{ color: 'rgba(255,255,255,0.88)', fontSize: '0.76rem', fontWeight: 600 }}>{i + 1} / {galleryUrls.length}</span>
                  </div>
                </div>
              </Reveal>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function TestimonialsSection({ s, testimonials }: { s: any; testimonials: TestimonialItem[] }) {
  return (
    <section style={{ background: 'var(--color-surface)', borderTop: '1px solid var(--color-border)', padding: 'clamp(64px,8vw,100px) var(--density-padding)' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        <Reveal>
          <div style={{ textAlign: 'center', marginBottom: '52px' }}>
            <Eyebrow label="Reviews" center />
            <h2 style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.6rem)', fontWeight: 'var(--font-heading-weight)', color: 'var(--color-text)', letterSpacing: '-0.03em', lineHeight: 1.15, margin: 0 }}>{s.testimonialsTitle || 'What Our Guests Say'}</h2>
          </div>
        </Reveal>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
          {testimonials.map((t, i) => {
            const initials = t.name?.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase() || '?';
            return (
              <Reveal key={i} delay={i * 80}>
                <div className="testimonial-card" style={{ background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 'calc(var(--radius) * 1.3)', padding: '28px 26px', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden', height: '100%' }}>
                  <div style={{ position: 'absolute', top: '10px', right: '18px', fontSize: '88px', lineHeight: 1, fontFamily: 'Georgia, serif', fontWeight: 700, color: 'var(--color-primary)', opacity: 0.055, pointerEvents: 'none', userSelect: 'none' }}>"</div>
                  <div style={{ display: 'flex', gap: '3px', marginBottom: '16px' }}>
                    {[...Array(t.rating || 5)].map((_, j) => <Star key={j} size={13} style={{ color: '#F59E0B', fill: '#F59E0B' }} />)}
                  </div>
                  <p style={{ fontSize: '0.895rem', color: 'var(--color-text)', lineHeight: 1.78, fontStyle: 'italic', margin: '0 0 22px', flex: 1 }}>"{t.body}"</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0, background: 'linear-gradient(135deg, var(--color-primary), var(--color-accent))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.72rem', fontWeight: 800 }}>{initials}</div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.855rem', color: 'var(--color-text)' }}>{t.name}</div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginTop: '2px' }}>Verified Guest</div>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CTASection({ slug, s, featureFlags, variant = 'cards' }: any) {
  if (variant === 'lined' || variant === 'numbered') {
    const solid = variant === 'numbered';
    return (
      <section style={{ background: solid ? 'var(--color-primary)' : 'var(--color-bg)', color: solid ? 'var(--color-primary-fg)' : 'var(--color-text)', borderTop: '1px solid var(--color-border)', padding: 'clamp(48px,6vw,80px) var(--density-padding)' }}>
        <Reveal>
          <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'flex', flexWrap: 'wrap', gap: '24px', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ maxWidth: '620px' }}>
              <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: 'var(--font-heading-weight)', letterSpacing: '-0.03em', lineHeight: 1.1, margin: '0 0 12px' }}>{s.ctaTitle || 'Ready to Experience Us?'}</h2>
              {s.ctaBody && <p style={{ fontSize: '1rem', opacity: 0.8, lineHeight: 1.65, margin: 0 }}>{s.ctaBody}</p>}
            </div>
            <Link href={getContextualHref(slug, s.ctaTarget === 'menu' ? '/menu' : '/reserve')} style={{ textDecoration: 'none' }}>
              <button className="primary-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '16px 36px', borderRadius: 'var(--radius)', background: solid ? '#fff' : 'var(--color-primary)', color: solid ? 'var(--color-primary)' : 'var(--color-primary-fg)', fontWeight: 800, fontSize: '1rem', border: 'none', cursor: 'pointer' }}>
                {browseLabel(s.ctaButtonLabel, 'Reserve Now', featureFlags?.orderingEnabled !== false)} <ArrowRight size={16} />
              </button>
            </Link>
          </div>
        </Reveal>
      </section>
    );
  }
  if (variant === 'tiles') {
    return (
      <section style={{ padding: 'clamp(56px,7vw,96px) var(--density-padding)', background: 'var(--color-bg)' }}>
        <Reveal>
          <div style={{ maxWidth: '920px', margin: '0 auto', border: '2px solid var(--color-text)', borderRadius: 'calc(var(--radius) * 1.5)', padding: 'clamp(36px,5vw,64px)', textAlign: 'center' }}>
            <h2 style={{ fontSize: 'clamp(1.7rem, 4vw, 2.7rem)', fontWeight: 'var(--font-heading-weight)', letterSpacing: '-0.03em', lineHeight: 1.1, margin: '0 0 14px', color: 'var(--color-text)' }}>{s.ctaTitle || 'Ready to Experience Us?'}</h2>
            {s.ctaBody && <p style={{ color: 'var(--color-text-muted)', lineHeight: 1.65, margin: '0 auto 30px', maxWidth: '460px' }}>{s.ctaBody}</p>}
            <Link href={getContextualHref(slug, s.ctaTarget === 'menu' ? '/menu' : '/reserve')} style={{ textDecoration: 'none' }}>
              <button className="primary-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '15px 34px', borderRadius: 'var(--radius)', background: 'var(--color-primary)', color: 'var(--color-primary-fg)', fontWeight: 800, border: 'none', cursor: 'pointer' }}>
                {browseLabel(s.ctaButtonLabel, 'Reserve Now', featureFlags?.orderingEnabled !== false)} <ArrowRight size={16} />
              </button>
            </Link>
          </div>
        </Reveal>
      </section>
    );
  }
  return (
    <section style={{ padding: 'clamp(64px,8vw,100px) var(--density-padding)', background: 'var(--color-bg)' }}>
      <Reveal>
        <div style={{ maxWidth: '920px', margin: '0 auto', background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-accent) 100%)', borderRadius: 'calc(var(--radius) * 2)', padding: 'clamp(52px,7vw,80px) clamp(32px,5vw,88px)', textAlign: 'center', color: '#fff', position: 'relative', overflow: 'hidden', boxShadow: '0 28px 88px rgba(0,0,0,0.14)' }}>
          <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.07) 1px, transparent 1px)', backgroundSize: '26px 26px', pointerEvents: 'none' }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '60px', height: '60px', borderRadius: '50%', background: 'rgba(255,255,255,0.14)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', marginBottom: '22px' }}>
              <Sparkles size={26} />
            </div>
            <h2 style={{ fontSize: 'clamp(1.75rem, 4vw, 2.9rem)', fontWeight: 'var(--font-heading-weight)', letterSpacing: '-0.03em', lineHeight: 1.1, margin: '0 0 14px' }}>
              {s.ctaTitle || 'Ready to Experience Us?'}
            </h2>
            {s.ctaBody && <p style={{ fontSize: '1.02rem', opacity: 0.84, lineHeight: 1.65, margin: '0 auto 38px', maxWidth: '460px' }}>{s.ctaBody}</p>}
            <Link href={getContextualHref(slug, s.ctaTarget === 'menu' ? '/menu' : '/reserve')} style={{ textDecoration: 'none' }}>
              <button className="primary-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '16px 38px', borderRadius: 'var(--radius)', background: '#fff', color: 'var(--color-primary)', fontWeight: 800, fontSize: '1rem', border: 'none', cursor: 'pointer', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
                {browseLabel(s.ctaButtonLabel, 'Reserve Now', featureFlags?.orderingEnabled !== false)} <ArrowRight size={16} />
              </button>
            </Link>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

function ContactSection({ restaurant, data }: any) {
  const hours = formatBusinessHoursRange(data?.restaurant?.config?.businessHours);
  const items = [
    restaurant.address ? { icon: MapPin, label: 'Address', value: restaurant.address } : null,
    restaurant.phone ? { icon: Phone, label: 'Phone', value: restaurant.phone } : null,
    { icon: Clock, label: 'Hours', value: hours },
  ].filter(Boolean) as { icon: any; label: string; value: string }[];

  return (
    <section style={{ background: 'var(--color-surface)', borderTop: '1px solid var(--color-border)', padding: 'clamp(56px,7vw,88px) var(--density-padding)' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        <Reveal>
          <div style={{ textAlign: 'center', marginBottom: '44px' }}>
            <Eyebrow label="Visit Us" center />
            <h2 style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.6rem)', fontWeight: 'var(--font-heading-weight)', color: 'var(--color-text)', letterSpacing: '-0.03em', lineHeight: 1.15, margin: 0 }}>Find Us Today</h2>
          </div>
        </Reveal>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '16px', maxWidth: '840px', margin: '0 auto' }}>
          {items.map((item, i) => (
            <Reveal key={i} delay={i * 80}>
              <div className="info-card" style={{ background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 'calc(var(--radius) * 1.3)', padding: 'clamp(22px, 3vw, 32px)', textAlign: 'center', boxShadow: 'var(--shadow-card)' }}>
                <div style={{ width: '54px', height: '54px', borderRadius: '50%', background: 'color-mix(in srgb, var(--color-primary) 11%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', color: 'var(--color-primary)' }}>
                  <item.icon size={21} />
                </div>
                <div style={{ fontSize: '0.66rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--color-text-muted)', marginBottom: '7px' }}>{item.label}</div>
                <div style={{ fontSize: '0.9rem', color: 'var(--color-text)', fontWeight: 500, lineHeight: 1.55 }}>{item.value}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function HomeFooter({ restaurant, slug, featureFlags, safeLogoUrl }: any) {
  const year = new Date().getFullYear();
  return (
    <footer style={{ background: 'var(--color-surface)', borderTop: '1px solid var(--color-border)' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: 'clamp(36px,4vw,52px) var(--density-padding) clamp(28px,3vw,36px)', display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: '28px' }}>
        <div style={{ maxWidth: '320px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            {safeLogoUrl && <img src={safeLogoUrl} alt="" style={{ width: '34px', height: '34px', borderRadius: '50%', objectFit: 'cover', border: '1.5px solid var(--color-primary)' }} />}
            <span style={{ fontWeight: 800, fontSize: '1.02rem', color: 'var(--color-text)', letterSpacing: '-0.015em' }}>{restaurant.name}</span>
          </div>
          {restaurant.description && <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', lineHeight: 1.65, margin: 0 }}>{restaurant.description.slice(0, 110)}{restaurant.description.length > 110 ? '…' : ''}</p>}
        </div>
        <nav style={{ display: 'flex', gap: '28px', alignItems: 'center', flexWrap: 'wrap' }}>
          <Link href={getContextualHref(slug, '/')} className="footer-link" style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', textDecoration: 'none', fontWeight: 600 }}>Home</Link>
          <Link href={getContextualHref(slug, '/menu')} className="footer-link" style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', textDecoration: 'none', fontWeight: 600 }}>Menu</Link>
          {featureFlags?.enableReservations && (
            <Link href={getContextualHref(slug, '/reserve')} className="footer-link" style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', textDecoration: 'none', fontWeight: 600 }}>Reserve</Link>
          )}
        </nav>
      </div>
      <div style={{ borderTop: '1px solid var(--color-border)', padding: '14px var(--density-padding)', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <span style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>© {year} {restaurant.name} · All rights reserved</span>
        <PoweredByDineator />
      </div>
    </footer>
  );
}
